import { useFormik } from 'formik'
import { get } from 'idb-keyval'
import { submitCreateMap } from 'map-creator'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { MapSaveDrawer as Drawer } from 'ui-components'
import { useAuth, useAuthWorkspaces } from 'user-auth'
import { getMapLink } from 'utils'
import * as yup from 'yup'

import { uploadMapThumbnail } from '~map-preview/api/uploadMapThumbnail'
import { setIsMapSaved, setMapData, useMap } from '~map-preview/states/map'
import {
  closeMapSaveDrawer,
  useIsMapSaveDrawerOpen
} from '~map-preview/states/ui'
import {
  CreateMapInput,
  Map_Type
} from '~map-preview/types/__generated/gql/graphql'

import { useCaptureMapThumbnail } from '~map-preview/hooks/useCaptureMapThumbnail'

const MapSaveDrawer = () => {
  const router = useRouter()
  const dispatch = useDispatch()

  const open = useIsMapSaveDrawerOpen()
  const forceOpen = router.query?.save === 'true'

  const mapData = useMap()
  const { user } = useAuth()
  const workspaces = useAuthWorkspaces()

  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      thumbnail: '',
      workspaceId: mapData?.workspaceId as string
    },
    validationSchema: yup.object().shape({
      name: yup.string().required('Name is required'),
      thumbnail: yup.string().required('Thumbnail is required'),
      workspaceId: yup.string().required('Workspace id is required')
    }),
    onSubmit: async (values, helpers) => {
      if (!user) throw new Error('Must be logged in')

      const { name, thumbnail, workspaceId } = values

      if (!workspaceId) throw new Error('Must have a default workspace')

      const geojson = await get(mapData.dataId)

      const mapInput: CreateMapInput = {
        id: mapData.id,
        dataId: mapData.dataId,
        properties: mapData.properties as any,
        bbox: mapData.bbox,
        centroid: mapData.centroid,
        workspaceId: mapData.workspaceId,
        type: Map_Type.Points,
        views: 1,
        likes: 0,
        remixes: 0,
        name
      }

      await uploadMapThumbnail({ mapId: mapData.id, blobPath: thumbnail })

      const createdMap = await submitCreateMap(
        { dataId: mapData.dataId, geojson, dataIdentifiers: [] },
        mapInput
      )

      dispatch(setMapData(createdMap))
      dispatch(setIsMapSaved(true))

      await router.replace(
        getMapLink({
          mapId: mapInput.id,
          mapName: mapInput.name
        })
      )

      helpers.resetForm()
    }
  })

  const mapThumbnail = useCaptureMapThumbnail()

  useEffect(() => {
    if (mapThumbnail) form.setFieldValue('thumbnail', mapThumbnail)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapThumbnail])

  const onClose = () => {
    if (router.query?.save) {
      const query = { ...router.query }
      delete query.save
      router.replace({ query })
    }

    dispatch(closeMapSaveDrawer())
  }

  return (
    <Drawer
      form={form}
      // workspaces={workspaces}
      open={open || forceOpen}
      onClose={onClose}
    />
  )
}

export { MapSaveDrawer }
